import { EProvince } from '@if/core/types'
import { ControlledPostalCodeField, ControlledSelect, ControlledTextField, useTranslatedOptionEnum } from '@if/mui'
import { Grid } from '@mui/material'
import { useYearOptions } from '@src/containers/ViewCreditApplicationPage/credit-hooks'
import { DefaultProgramConfig } from '@src/types/Constants'
import { Control, FieldPath, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  isCommercial?: boolean
  formControl: Control<TFieldValues>
  includeDuration?: boolean
  supportedProvinces?: EProvince[]
  disableEdit?: boolean
}

const AddressComponent = <TFieldValues extends FieldValues = FieldValues>({
  name,
  isCommercial = false,
  formControl,
  includeDuration = true,
  supportedProvinces = DefaultProgramConfig.supportedProvinces,
  disableEdit,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const provinceList = useTranslatedOptionEnum('EProvince', supportedProvinces, 'enum')
  const yearOptions = useYearOptions(15)

  return (
    <>
      <Grid container spacing={1} rowSpacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 3,
            lg: 2,
          }}
        >
          <ControlledTextField
            name={`${name}.civicNumber` as FieldPath<TFieldValues>}
            control={formControl}
            id="civicNumver"
            label={t('editCreditApplication.address.addressNumber')}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 10 } }}
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 8,
          }}
        >
          <ControlledTextField
            id="street"
            name={`${name}.street` as FieldPath<TFieldValues>}
            control={formControl}
            label={t('editCreditApplication.address.street')}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        {!isCommercial && (
          <Grid
            size={{
              xs: 12,
              sm: 4,
              md: 3,
              lg: 2,
            }}
          >
            <ControlledTextField
              name={`${name}.apartment` as FieldPath<TFieldValues>}
              id="apartment"
              control={formControl}
              label={`${t('editCreditApplication.address.apartment')} (${String(t('common.ifApplicable'))})`}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              fullWidth
              disabled={disableEdit}
            />
          </Grid>
        )}
        {isCommercial && (
          <Grid
            size={{
              xs: 12,
              sm: 4,
              md: 3,
              lg: 2,
            }}
          >
            <ControlledTextField
              name={`${name}.suite` as FieldPath<TFieldValues>}
              control={formControl}
              id="suite"
              label={t('editCreditApplication.address.suite')}
              slotProps={{ htmlInput: { maxLength: 10 } }}
              fullWidth
              disabled={disableEdit}
            />
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 8,
            md: 4,
          }}
        >
          <ControlledTextField
            name={`${name}.city` as FieldPath<TFieldValues>}
            control={formControl}
            id="city"
            label={t('editCreditApplication.address.city')}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <ControlledPostalCodeField
            id="postalCode"
            name={`${name}.postalCode` as FieldPath<TFieldValues>}
            control={formControl}
            label={t('editCreditApplication.address.postalCode')}
            fullWidth
            disabled={disableEdit}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <ControlledSelect
            name={`${name}.stateIso` as FieldPath<TFieldValues>}
            id="province"
            control={formControl}
            options={provinceList}
            label={t('editCreditApplication.address.province')}
            disableEmptyOption
            fullWidth
            disabled={disableEdit}
          />
        </Grid>
        {includeDuration && (
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <ControlledSelect
              id="addressSinceYears"
              name={`${name}.years` as FieldPath<TFieldValues>}
              control={formControl}
              label={t('editCreditApplication.address.since.years')}
              fullWidth
              options={yearOptions}
              disabled={disableEdit}
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default AddressComponent
